import { FormattedMessage, useIntl } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import ExpanderPanel from '@rio-cloud/rio-uikit/components/expander/ExpanderPanel';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';

import InternalErrorState from '@/features/app/InternalErrorState';
import { useFetchBatteryPackLevelsQuery } from '@/features/battery/api/batterySlice';
import { getDaysInYearsAndMonths } from '@/features/battery/utils/DateUtils';
import { NO_DATA } from '@/features/batteryOverview/utils/BatteryDataUtils';
import { BatteryPack } from '@/generated/GeneratedApiTypes';
import { BatteryDetailsRow } from './BatteryDetailsRow';
import ExpanderTitle from './ExpanderTitle';

const KWH = ' kWh';

type PackLevelViewProps = {
    assetId: string;
    hasServiceCareM: boolean;
};

const PackDetails = ({ pack, hasServiceCareM }: { pack: BatteryPack; hasServiceCareM: boolean }) => {
    const intl = useIntl();
    const usage = getDaysInYearsAndMonths(pack.usage_days, intl);

    return (
        <div className="display-grid detail">
            <BatteryDetailsRow
                name="intl-msg:batteryService.batteryPackLevel.packMileage"
                value={pack.mileage}
                unit=" km"
            />
            <BatteryDetailsRow name="intl-msg:batteryService.batteryPackLevel.packAge" value={usage} unit="" />
            {hasServiceCareM && (
                <BatteryDetailsRow
                    name="intl-msg:batteryService.fullChargeEnergy"
                    value={pack.full_charge_energy}
                    unit={KWH}
                    tooltipMsg="intl-msg:batteryService.fullChargeEnergy.tooltip"
                />
            )}
            <BatteryDetailsRow
                name="intl-msg:batteryService.dischargedEnergyThroughput"
                value={pack.discharging_energy_throughput}
                unit={KWH}
                tooltipMsg="intl-msg:batteryService.dischargedEnergyThroughput.tooltip.body"
            />
        </div>
    );
};

type PackExpanderProps = { pack: BatteryPack; index: number; hasServiceCareM: boolean };

const PackExpander = ({ pack, index, hasServiceCareM }: PackExpanderProps) => {
    const status = pack.state_of_health_status?.toUpperCase() ?? NO_DATA;

    return (
        <div className="margin-bottom-5">
            <div className="margin-bottom-5 text-color-dark">
                <FormattedMessage id="intl-msg:batteryService.batteryPackLevel.pack" /> {index + 1}
            </div>
            <ExpanderPanel
                title={
                    <ExpanderTitle percentage={pack.state_of_health_percentage} systemStateOfHealthStatus={status} />
                }
                titleClassName="width-100pct"
                headerClassName="width-100pct padding-left-0 padding-top-0 padding-bottom-0"
                bodyClassName="bg-white"
                className="border-style-solid border-width-1 border-color-light"
                open={false}
            >
                <div className="panel-container">
                    <div className="panel-body padding-0">
                        <PackDetails pack={pack} hasServiceCareM={hasServiceCareM} />
                    </div>
                </div>
            </ExpanderPanel>
        </div>
    );
};

const PackLevelLoadingScreen = () => {
    const marginTopBottom10 = 'margin-top-10 margin-bottom-10';
    return (
        <div className="display-flex flex-column" data-testid="pack-level-loading">
            <ContentLoader height={60} width="100%" className={marginTopBottom10} />
            <ContentLoader height={60} width="100%" className={marginTopBottom10} />
            <ContentLoader height={60} width="100%" className={marginTopBottom10} />
        </div>
    );
};

const PackLevelView = ({ assetId, hasServiceCareM }: PackLevelViewProps) => {
    const { isLoading, isError, error, data: batteryPacks, refetch } = useFetchBatteryPackLevelsQuery(assetId);

    if (isLoading) {
        return <PackLevelLoadingScreen />;
    }

    if (isError) {
        return (
            <InternalErrorState
                errorCode={(error as FetchBaseQueryError).status as number}
                reloadTriggerFunction={refetch}
                isCustomerFrontend
            />
        );
    }

    if (!batteryPacks || batteryPacks.packs.length === 0) {
        return null;
    }

    return (
        <>
            {batteryPacks.packs.map((pack, index) => (
                <PackExpander key={pack.id ?? index} pack={pack} index={index} hasServiceCareM={hasServiceCareM} />
            ))}
        </>
    );
};

export default PackLevelView;
